import { useCallback, useEffect, useState } from 'react';

import { apiRebox } from '@services/index';

import CardDashboardCommercialDefault from '../../Default/CardDashboardCommercialDefault';
import DashboardColumns from '../../Default/DashboardColumnsDefault';
import useGetDateFormatted from '../../hooks/get-date-formatted';
import useWindowDimensions from '../../hooks/get-window-dimensions';
import { IPropsDashboards, selectDashboardDateTypes } from '../typings';

import { Container } from './styles';

interface IContractsSummary {
  total: number;
  by_status: Record<string, number>;
  by_current_payments_status: Record<string, number>;
}

const DashboardCommercial: React.FC<IPropsDashboards> = ({
  dashboardDateTypesMobile,
  setLoadingDashboard,
  firstLoadingDashboard,
  setFirstLoadingDashboard,
}) => {
  // Para dados do dashboard
  const [
    dataDashboardOverview,
    setDataDashboardOverview,
  ] = useState<IContractsSummary>({
    total: 0,
    by_status: {},
    by_current_payments_status: {},
  });
  // Para formatação de dados
  const [dataOverviewFormatted, setDataOverviewFormatted] = useState({
    overview: {
      tpa: 0,
      tpat: 0,
      tpag: 0,
    },
  });
  const [loadingOverview, setLoadingOverview] = useState<boolean>(false);

  const { width } = useWindowDimensions();

  const {
    formattedToday,
    formattedYesterday,
    formattedCurrentMonth,
    formattedLastMonth,
    formattedCurrentYear,
  } = useGetDateFormatted();
  const days: Array<selectDashboardDateTypes> = [
    'hoje',
    'ontem',
    'mês atual',
    'mês passado',
    'ano',
  ];

  const whichDate = (dayWitch: selectDashboardDateTypes) => {
    return days.find(day => dayWitch === day);
  };

  const date = [
    {
      period_start: formattedToday().formattedTodayStart,
      period_end: formattedToday().formattedTodayEnd,
      nameDay: whichDate('hoje'),
    },
    {
      period_start: formattedYesterday().formattedYesterdayStart,
      period_end: formattedYesterday().formattedYesterdayEnd,
      nameDay: whichDate('ontem'),
    },
    {
      period_start: formattedCurrentMonth().formattedThisMonthFirstDay,
      period_end: formattedCurrentMonth().formattedThisMonthLastDay,
      nameDay: whichDate('mês atual'),
    },
    {
      period_start: formattedLastMonth().formattedLastMonthFirstDay,
      period_end: formattedLastMonth().formattedLastMonthLastDay,
      nameDay: whichDate('mês passado'),
    },
    {
      period_start: formattedCurrentYear().formattedThisYearFirstDay,
      period_end: formattedCurrentYear().formattedThisYearLastDay,
      nameDay: whichDate('ano'),
    },
  ];

  // switch (dashboardDateTypesMobile) {
  //   case whichDate('hoje'):
  //     period_start_mobile = formattedToday().formattedTodayStart;
  //     period_end_mobile = formattedToday().formattedTodayEnd;
  //     break;
  //   case whichDate('ontem'):
  //     period_start_mobile = formattedYesterday().formattedYesterdayStart;
  //     period_end_mobile = formattedYesterday().formattedYesterdayEnd;
  //     break;
  //   case whichDate('mês atual'):
  //     period_start_mobile = formattedCurrentMonth().formattedThisMonthFirstDay;
  //     period_end_mobile = formattedCurrentMonth().formattedThisMonthLastDay;
  //     break;
  //   case whichDate('mês passado'):
  //     period_start_mobile = formattedLastMonth().formattedLastMonthFirstDay;
  //     period_end_mobile = formattedLastMonth().formattedLastMonthLastDay;
  //     break;
  //   case whichDate('ano'):
  //     period_start_mobile = formattedCurrentYear().formattedThisYearFirstDay;
  //     period_end_mobile = formattedCurrentYear().formattedThisYearLastDay;
  //     break;
  //   default:
  //     return null;
  // }

  const period_start_and_end_mobile = useCallback(() => {
    // for (let i = 0; i < date.length; i++) {
    //   case whichDate(date[i].nameDay):
    //     period_start_mobile = date[i].period_start;
    //     period_end_mobile = date[i].period_end;
    //     break;
    //   default:
    //     return null;
    // }
    // for (const element of date) {
    //   switch (dashboardDateTypesMobile) {
    //     case whichDate(element.nameDay as selectDashboardDateTypes):
    //       return {
    //         period_start_mobile: element.period_start,
    //         period_end_mobile: element.period_end,
    //       };
    //     default:
    //       return {
    //         period_start_mobile: formattedToday().formattedTodayStart,
    //         period_end_mobile: formattedToday().formattedTodayEnd,
    //       };
    //   }
    // }
    for (const element of date) {
      if (element.nameDay === dashboardDateTypesMobile) {
        return {
          period_start_mobile: element.period_start,
          period_end_mobile: element.period_end,
        };
      }
    }
    return {
      period_start_mobile: formattedToday().formattedTodayStart,
      period_end_mobile: formattedToday().formattedTodayEnd,
    };
  }, [dashboardDateTypesMobile]);

  const dataFormatted = useCallback(() => {
    setDataOverviewFormatted({
      overview: {
        tpa: dataDashboardOverview.by_current_payments_status.IN_DAY ?? 0,
        tpat: dataDashboardOverview.by_current_payments_status.OVERDUE ?? 0,
        tpag: dataDashboardOverview.by_status.PENDING ?? 0,
      },
    });
  }, [dataDashboardOverview]);

  const getDataDashboardOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);

      const { data: response } = await apiRebox.get(`/contracts/summary`);

      setDataDashboardOverview(response.data);
      setLoadingOverview(false);
    } catch (error) {
      console.error(
        `Erro ao tentar buscar dados para o dashboard overview: ${error}`,
        error,
      );
      setLoadingOverview(false);
    }
  }, []);

  useEffect(() => {
    getDataDashboardOverview();
    const interval = setInterval(getDataDashboardOverview, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    dataFormatted();
  }, [dataDashboardOverview]);

  return (
    <Container>
      {width < 981 ? (
        <CardDashboardCommercialDefault
          period_start={period_start_and_end_mobile()?.period_start_mobile}
          period_end={period_start_and_end_mobile()?.period_end_mobile}
          dataOverview={{
            tpa: dataOverviewFormatted.overview.tpa,
            tpat: dataOverviewFormatted.overview.tpat,
            tpag: dataOverviewFormatted.overview.tpag,
            loadingOverview,
          }}
          IpropsDashboards={{
            dashboardDateTypesMobile,
            setLoadingDashboard,
            setFirstLoadingDashboard,
            firstLoadingDashboard,
          }}
          className="card-mobile"
          id="card-mobile"
        />
      ) : (
        <DashboardColumns>
          {date.map((day, index) => (
            <CardDashboardCommercialDefault
              key={`${index}-${day}`}
              period_start={day.period_start}
              period_end={day.period_end}
              dataOverview={{
                tpa: dataOverviewFormatted.overview.tpa,
                tpat: dataOverviewFormatted.overview.tpat,
                tpag: dataOverviewFormatted.overview.tpag,
                loadingOverview,
              }}
              IpropsDashboards={{
                dashboardDateTypesDesktop: day.nameDay,
                setLoadingDashboard,
                setFirstLoadingDashboard,
                firstLoadingDashboard,
              }}
              className="card-desktop"
              id="card-desktop"
            />
          ))}
        </DashboardColumns>
      )}
    </Container>
  );
};

export default DashboardCommercial;
